// Pure payload builders extracted from SpeedyForm for testability (US1760811 PR review #4).
// Behavior and payload shapes here MUST remain byte-for-byte identical to the original inline
// object literals in SpeedyForm.js. These are pure functions of their inputs (no side effects,
// no component state, no API calls) so they can be unit-tested in isolation.

// sentenceCase upper-cases only the FIRST character of a message, leaving the rest untouched.
//
// Server error strings are lowercase by Go convention (staticcheck ST1005 enforces it), but
// SpeedyForm renders them verbatim to the user — e.g. the assignment-group rejection from
// VerifyEOCBridgeINC, which reaches the UI as "incident is not assigned to an appropriate
// assignment group". Capitalizing just the first character makes it read as a sentence without
// touching embedded identifiers, and is a no-op for a message that already starts with one
// (e.g. "INC4449471 ...") or is already capitalized.
//
// Non-strings and empty strings pass through unchanged so the caller's `||` fallback still fires.
export const sentenceCase = (message) =>
  typeof message === "string" && message.length > 0
    ? message.charAt(0).toUpperCase() + message.slice(1)
    : message;

// buildSpeedyProblemRecord builds the problem record POSTed to /api/v1/problem. The optional
// `number` field is only included when a PRB number was provided (trimmed non-empty).
export const buildSpeedyProblemRecord = (formData) => {
  const problemRecord = {
    description: formData.currentStatus,
    outages: {
      "OUTAGE 1": {
        number: "OUTAGE 1",
        description: formData.currentStatus,
        short_description: formData.title,
        impactedApplications: formData.applicationsImpacted,
        affectedBrandCapability: formData.brandImpacted,
        priority: "EP4", // Default priority for speedy form
        status: "Log / Classify", // Default initial status
        componentID: formData.component ? formData.component.value : "",
        statusPageGroupID: formData.statusPageGroup
          ? formData.statusPageGroup.value
          : "",
        notes: [],
      },
    },
    temporary: false,
    communications: [],
    auditTrail: {
      changeLog: [],
    },
  };

  // Only include the number field if a PRB number was provided
  if (formData.prbNumber && formData.prbNumber.trim()) {
    problemRecord.number = formData.prbNumber.trim();
  }

  return problemRecord;
};

// buildSpeedyAdvisoryPayload builds the advisory communication payload posted via
// CommunicationAPI.attachCommunication. `sender` is { name, email }; `publishingTargets` is the
// array of selected status-page routes.
export const buildSpeedyAdvisoryPayload = (formData, sender, publishingTargets) => ({
  template: "Advisory",
  subject: formData.title,
  currentStatus: formData.currentStatus,

  affectedUsers: "", // SpeedyForm doesn't capture affected users - will be updated via main form
  applications: formData.applicationsImpacted.map((app) => app.label),
  affectedBrandCapability: formData.brandImpacted,
  // Updated to use publishingTargets array for multiple status page components
  statusPageRoutes: publishingTargets,
  sendStatus: "SENT",
  // Include sender information in communication payload
  sender: sender,
});

// buildSpeedyPreviewContent builds the preview content shown before creating the PRB. Pure
// function of formData plus the selected publishingTargets array.
export const buildSpeedyPreviewContent = (formData, publishingTargets) => {
  const subject = formData.title;

  return {
    subject,
    currentStatus: formData.currentStatus,
    applications: formData.applicationsImpacted.map((app) => app.label),
    brandImpacted: formData.brandImpacted,
    // Updated to use publishingTargets array
    publishingTargets: publishingTargets,
  };
};
